<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Divider Component
      p.text--secondary A versatile divider component with customizable color and width.

    // Divider Examples
    .component-section.mb-8#basic-divider
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-divider')") Basic Divider Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in dividerVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Divider Implementation
            .pa-4(v-if="!variation.props.vertical")
              div Content above divider
              yz-divider(v-bind="variation.props")
              div Content below divider
            .pa-4.d-flex.align-center(v-else style="min-height: 80px;")
              span Left content
              yz-divider.mx-3(v-bind="variation.props")
              span Right content

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in dividerProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

</template>

<script>
export default {
  data() {
    return {
      copiedCode: null
    }
  },
  computed: {
    dividerVariations() {
      return [
        {
          name: 'Default Divider',
          id: 'default-divider-example',
          description: 'Standard divider with neutral color',
          label: 'Default Divider',
          hint: 'yz-neutral-300 color, 1px width',
          props: {},
          code: `yz-divider`
        },
        {
          name: 'Custom Color',
          id: 'color-divider-example',
          description: 'Divider with custom color',
          label: 'Custom Color',
          hint: 'Using yz-danger color',
          props: {
            color: 'yz-danger'
          },
          code: `yz-divider(color="yz-danger")`
        },
        {
          name: 'Hex Color',
          id: 'hex-divider-example',
          description: 'Divider with hex color',
          label: 'Hex Color',
          hint: 'Using #3498db color',
          props: {
            color: '#3498db'
          },
          code: `yz-divider(color="#3498db")`
        },
        {
          name: 'Custom Width (Number)',
          id: 'width-number-example',
          description: 'Divider with custom width using number',
          label: 'Custom Width (3px)',
          hint: 'Width as number: 3',
          props: {
            width: 3
          },
          code: `yz-divider(:width="3")`
        },
        {
          name: 'Custom Width (String)',
          id: 'width-string-example',
          description: 'Divider with custom width using string',
          label: 'Custom Width (2px)',
          hint: 'Width as string: "2px"',
          props: {
            width: '2px'
          },
          code: `yz-divider(width="2px")`
        },
        {
          name: 'Thick Colored Divider',
          id: 'thick-colored-example',
          description: 'Combination of custom color and width',
          label: 'Thick Colored',
          hint: 'Primary color with 4px width',
          props: {
            color: 'yz-success',
            width: 4
          },
          code: `yz-divider(color="yz-success" :width="4")`
        },
        {
          name: 'Vertical Divider',
          id: 'vertical-example',
          description: 'Vertical divider orientation',
          label: 'Vertical Divider',
          hint: 'Using vertical prop',
          props: {
            vertical: true,
            color: 'yz-danger-500',
            height: 50
          },
          code: `yz-divider.mx-3(vertical color="yz-danger-500" :height="50")`
        }
      ]
    },
    dividerProps() {
      return [
        {
          name: 'color',
          type: 'String',
          default: 'yz-neutral-300',
          description: 'Color of the divider. Supports CSS variables (yz-neutral-300), hex colors (#3498db), or CSS variables (var(--color))'
        },
        {
          name: 'width',
          type: 'String | Number',
          default: '1',
          description: 'Width/thickness of the divider. Number values are converted to px (e.g., 2 becomes "2px")'
        },
        {
          name: 'height',
          type: 'String | Number',
          default: 'null',
          description: 'Height of the divider (only applies to vertical dividers). Number values are converted to px'
        },
        {
          name: 'vertical',
          type: 'Boolean',
          default: 'false',
          description: 'Creates a vertical divider instead of horizontal'
        },
        {
          name: 'inset',
          type: 'Boolean',
          default: 'false',
          description: 'Adds left margin to the divider'
        }
      ]
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>